import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, TrendingUp, Share2 } from "lucide-react";
import api from "@/lib/api";

// Climb Nudge — shows a creator where their current game ranks in its genre on the community
// leaderboard, with a "climb the board" prompt to drive repeat sessions. Hidden while loading.
export default function ClimbNudge({ gameId }) {
  const navigate = useNavigate();
  const [r, setR] = useState(undefined); // undefined = loading · null = unranked · object = ranked

  useEffect(() => {
    if (!gameId) return;
    let live = true;
    api.get(`/compiler/${gameId}/rank`).then(({ data }) => live && setR(data)).catch(() => live && setR(null));
    return () => { live = false; };
  }, [gameId]);

  if (r === undefined) return null;

  const shareRank = () => {
    const og = `${window.location.origin}/api/og/rank/${gameId}`;
    if (typeof navigator !== "undefined" && navigator.share) navigator.share({ url: og }).catch(() => {});
    else navigator.clipboard?.writeText(og).catch(() => {});
    window.open(`${window.location.origin}/api/og/rank/${gameId}/image.png`, "_blank", "noopener");
  };

  if (!r) {
    return (
      <button onClick={() => navigate("/leaderboard")} data-testid="climb-nudge"
        className="w-full flex items-center gap-2.5 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-2.5 text-left hover:border-[hsl(var(--primary))]/40 transition-colors">
        <Trophy className="w-4 h-4 text-amber-400 shrink-0" />
        <span className="text-[13px] text-[hsl(var(--muted-foreground))] flex-1">Publish this game to claim a spot on the community leaderboard.</span>
        <span className="text-[12px] font-semibold text-[hsl(var(--primary))] shrink-0">See the board →</span>
      </button>
    );
  }

  const top1 = r.rank === 1;
  const top3 = r.rank <= 3;
  const accent = top1 ? "#fbbf24" : top3 ? "#38bdf8" : "hsl(var(--primary))";
  const msg = top1
    ? `You're #1 in ${r.label} — defend your crown!`
    : top3
      ? `You're #${r.rank} of ${r.count} in ${r.label}. One more polish pass could take #1.`
      : `You're #${r.rank} of ${r.count} in ${r.label}. Climb the board!`;

  return (
    <div data-testid="climb-nudge" className="w-full flex items-center gap-3 rounded-2xl border px-4 py-2.5"
      style={{ borderColor: top1 ? "#fbbf2455" : "hsl(var(--border))", background: top1 ? "#fbbf240d" : "hsl(var(--card))" }}>
      {top1 ? <Trophy className="w-4 h-4 shrink-0" style={{ color: accent }} /> : <TrendingUp className="w-4 h-4 shrink-0" style={{ color: accent }} />}
      <div className="text-[13px] font-semibold flex-1 min-w-0 truncate" data-testid="climb-nudge-msg">{msg}</div>
      {top3 && (
        <button onClick={shareRank} data-testid="climb-nudge-share" className="hidden sm:inline-flex items-center gap-1 text-[12px] font-semibold shrink-0" style={{ color: accent }}>
          <Share2 className="w-3 h-3" /> {top1 ? "Share #1" : "Share"}
        </button>
      )}
      <button onClick={() => navigate("/leaderboard")} data-testid="climb-nudge-cta" className="text-[12px] font-semibold text-[hsl(var(--primary))] shrink-0 hover:underline">Climb the board →</button>
    </div>
  );
}
